import { useState } from 'react'
import { ErrorState, LoadingState } from '../components/StateViews'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'

type Step = {
  id: string
  title: string
  done: boolean
  detail: string | null
  action?: { label: string; url: string | null; verb?: string; path?: string }
}

type Payload = { steps: Step[]; completed: number; total: number; dismissed: boolean }

/**
 * Onboarding checklist (SW-606).
 *
 * **Activation is the whole game.** A merchant who installs but never enables the app embed
 * sends zero alerts, sees zero value, and churns inside a week. Every step is detected rather
 * than ticked, and every step is instrumented so drop-off is measurable per step.
 */
export function Onboarding() {
  const { data, error, loading, reload } = useApi<Payload>('/onboarding')
  const [verifying, setVerifying] = useState(false)

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  async function verifyButton() {
    setVerifying(true)
    try {
      const result = await api.post<{ verified: boolean; message: string }>(
        '/onboarding/verify-button',
      )
      shopify.toast.show(result.message, { isError: !result.verified })
      reload()
    } catch {
      shopify.toast.show('Could not check your storefront', { isError: true })
    } finally {
      setVerifying(false)
    }
  }

  return (
    <s-stack direction="block" gap="large">
      <s-section>
        {/* The setup screen is the one place a merchant has not yet seen the product, so the
            mark earns its place here. Everything below stays Polaris, see StateViews. */}
        <s-stack direction="inline" gap="base" alignItems="center">
          <s-box maxInlineSize="64px">
            <s-image
              src="/brand/mark-512.png"
              alt=""
              inlineSize="fill"
              aspectRatio="1/1"
              objectFit="contain"
            />
          </s-box>
          <s-stack direction="block" gap="base">
            <s-heading>Set up StockWait</s-heading>
            <s-text color="subdued">
              {data.completed} of {data.total} done. The first two steps are the ones that matter:
              nothing reaches a shopper until the app embed is on.
            </s-text>
          </s-stack>
        </s-stack>
      </s-section>

      <s-section>
        <s-stack direction="block" gap="base">
          {data.steps.map((step) => (
            <s-box
              key={step.id}
              padding="base"
              borderRadius="base"
              background={step.done ? 'subdued' : undefined}
            >
              <s-stack
                direction="inline"
                gap="base"
                alignItems="start"
                justifyContent="space-between"
              >
                <s-stack direction="inline" gap="small-300" alignItems="start">
                  <s-icon
                    type={step.done ? 'check-circle-filled' : 'circle-dashed'}
                    tone={step.done ? 'success' : 'neutral'}
                  />
                  <s-stack direction="block" gap="small-500">
                    {step.done ? (
                      <s-text>{step.title}</s-text>
                    ) : (
                      <s-text type="strong">{step.title}</s-text>
                    )}
                    {step.detail ? <s-text color="subdued">{step.detail}</s-text> : null}
                  </s-stack>
                </s-stack>

                {!step.done && step.action ? (
                  step.action.url ? (
                    // Deep link straight into the theme editor with the embed pre-selected.
                    // "Go and find app embeds" is exactly where activation is lost.
                    <s-button href={step.action.url} target="_blank">
                      {step.action.label}
                    </s-button>
                  ) : (
                    <s-button disabled={verifying} onClick={verifyButton}>
                      {verifying ? 'Checking…' : step.action.label}
                    </s-button>
                  )
                ) : null}
              </s-stack>
            </s-box>
          ))}
        </s-stack>
      </s-section>
    </s-stack>
  )
}
